import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Grid, Stepper, Step, StepLabel, FormControlLabel, Switch } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import SectionCard from '../../../components/SectionCard';
import InvButton from '../../../components/InvButton';
import { InvTextField, InvSelect } from '../../../components/InvField';
import { getStores } from '../../../api/stockLedger';
import useInvRole from '../../../hooks/useInvRole';
import { INV_ACTIONS } from '../../../utils/inventoryRoles';
import { parseMoney } from '../../../utils';
import { fadeInSx } from '../pharmacyStyles';
import useDirectDispensePipeline from './useDirectDispensePipeline';
import DirectDispenseMedicineStep, {
  MEDICINE_LINE_DEFAULT,
  medicineLinesValid,
  isControlledMedicineLine,
} from './DirectDispenseMedicineStep';

const STEPS = ['Customer / patient', 'Medicines & payment'];

const GENDER_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
];

const EMPTY_PATIENT = { name: '', phone: '', age: '', gender: '', doctorName: '' };

/**
 * Unified walk-in / quick-register dispense.
 * Anonymous ON → walk-in (name optional). Anonymous OFF → quick register (name required).
 */
export default function DirectDispenseFlow({
  initialAnonymous = true,
  embedded = false,
  onBack,
}) {
  const pipeline = useDirectDispensePipeline();
  const { can } = useInvRole();

  const canWalkIn = can(INV_ACTIONS.DIRECTDISPENSE_WALKIN);
  const canQuickRegister = can(INV_ACTIONS.DIRECTDISPENSE_QUICKREGISTER);
  const showAnonymousToggle = canWalkIn && canQuickRegister;

  const forcedAnonymous = useMemo(() => {
    if (showAnonymousToggle) return null;
    if (canWalkIn) return true;
    if (canQuickRegister) return false;
    return true;
  }, [showAnonymousToggle, canWalkIn, canQuickRegister]);

  const [step, setStep] = useState(0);
  const [anonymous, setAnonymous] = useState(
    forcedAnonymous != null ? forcedAnonymous : Boolean(initialAnonymous),
  );
  const [patient, setPatient] = useState({ ...EMPTY_PATIENT });
  const [lines, setLines] = useState([{ ...MEDICINE_LINE_DEFAULT }]);
  const [storeId, setStoreId] = useState('');
  const [store, setStore] = useState(null);

  useEffect(() => {
    if (forcedAnonymous != null) setAnonymous(forcedAnonymous);
  }, [forcedAnonymous]);

  useEffect(() => {
    getStores({ is_pharmacy_store: true })
      .then((data) => {
        const storeList = Array.isArray(data) ? data : (data?.items || []);
        const first = storeList[0];
        if (first) {
          setStoreId(first.store_id || first.id || '');
          setStore(first);
        }
      })
      .catch(() => {});
  }, []);

  // A controlled/narcotic line cannot be sold to nobody: the service rejects a
  // controlled sale without a named patient, so the counter must quick-register
  // before it can go any further.
  const hasControlledLine = useMemo(() => lines.some(isControlledMedicineLine), [lines]);
  const controlledNeedsQuickRegister = hasControlledLine && !canQuickRegister;

  useEffect(() => {
    if (hasControlledLine && anonymous && canQuickRegister) {
      setAnonymous(false);
      setStep(0);
      toast.info('Controlled medicine selected — enter the patient name to continue');
    }
  }, [hasControlledLine, anonymous, canQuickRegister]);

  const patientValid = (anonymous && !hasControlledLine) || patient.name.trim().length >= 2;
  const doctorName = anonymous ? '' : patient.doctorName.trim();
  const patientLabel = anonymous
    ? 'Walk-in'
    : (patient.name.trim() || 'Walk-in');

  const patientDisplay = useMemo(() => (anonymous ? null : {
    name: patient.name.trim(),
    mobile: patient.phone.trim(),
    age: patient.age,
    gender: patient.gender,
  }), [anonymous, patient.name, patient.phone, patient.age, patient.gender]);

  function goBack() {
    if (step === 0) {
      if (onBack) onBack();
      return;
    }
    if (!pipeline.state.paymentResult) setStep((s) => s - 1);
    else if (onBack) onBack();
  }

  function buildRemarks() {
    const parts = [anonymous ? 'Walk-in dispense' : 'Quick register dispense'];
    if (doctorName) parts.push(`Doctor: ${doctorName}`);
    return parts.join(' · ');
  }

  async function prepareBillOnly() {
    if (!storeId) {
      toast.error('No pharmacy store available');
      throw new Error('No pharmacy store available');
    }
    if (!medicineLinesValid(lines)) {
      toast.error('Add at least one medicine with a valid quantity');
      throw new Error('Add at least one medicine with a valid quantity');
    }
    return pipeline.runPrepareBill({
      lines,
      storeId,
      store,
      patientDisplay: patientDisplay || {},
      remarks: buildRemarks(),
    });
  }

  async function prepareAndPay(modes) {
    if (!storeId) {
      toast.error('No pharmacy store available');
      return;
    }
    if (!medicineLinesValid(lines)) {
      toast.error('Add at least one medicine with a valid quantity');
      return;
    }

    const prepared = await prepareBillOnly();
    const payable = parseMoney(prepared?.amountPayable ?? pipeline.state.amountPayable);
    const allocated = (modes || []).reduce((sum, m) => sum + parseMoney(m.amount), 0);
    if (Math.abs(allocated - payable) > 0.05) {
      toast.error(
        `Bill priced at ₹ ${payable.toFixed(2)} but payment totals ₹ ${allocated.toFixed(2)}. Adjust payment modes and try again.`,
      );
      return;
    }

    let finalModes = modes;
    if (modes?.length && Math.abs(allocated - payable) > 0.0001) {
      const diff = payable - allocated;
      finalModes = modes.map((m, i) => (
        i === 0
          ? { ...m, amount: String((parseMoney(m.amount) + diff).toFixed(4)) }
          : m
      ));
    }

    await pipeline.collectPayment(finalModes);
  }

  function hardReset() {
    pipeline.resetPipeline();
    setAnonymous(forcedAnonymous != null ? forcedAnonymous : Boolean(initialAnonymous));
    setPatient({ ...EMPTY_PATIENT });
    setLines([{ ...MEDICINE_LINE_DEFAULT }]);
    setStep(0);
  }

  const showBack = !embedded || step > 0 || onBack;

  return (
    <Box sx={fadeInSx}>
      {showBack && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <InvButton variant="outlined" tone="teal" size="small" startIcon={<ArrowBackIcon />} onClick={goBack}>
            Back
          </InvButton>
        </Box>
      )}

      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {STEPS.map((label, i) => (
          <Step
            key={label}
            completed={i < step || !!pipeline.state.paymentResult}
            onClick={() => i < step && !pipeline.state.paymentResult && setStep(i)}
            sx={{ cursor: i < step && !pipeline.state.paymentResult ? 'pointer' : 'default' }}
          >
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 0 && (
        <SectionCard
          title="Direct dispense"
          subtitle={
            anonymous
              ? 'Anonymous walk-ins omit name/mobile on the bill'
              : 'Name and mobile feed the bill receipt only — no patient_id is sent'
          }
        >
          {controlledNeedsQuickRegister && (
            <Alert severity="error" sx={{ mb: 2 }}>
              This dispense contains a controlled / narcotic medicine, which needs a
              named patient. Your role can only raise anonymous walk-ins — remove the
              controlled medicine or ask a colleague with quick-register rights.
            </Alert>
          )}
          {hasControlledLine && !controlledNeedsQuickRegister && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Controlled medicine selected — anonymous walk-in is not allowed. Enter
              the patient name to continue.
            </Alert>
          )}
          {showAnonymousToggle && (
            <FormControlLabel
              control={
                <Switch
                  checked={anonymous}
                  onChange={(e) => setAnonymous(e.target.checked)}
                  color="primary"
                  disabled={hasControlledLine}
                />
              }
              label="Anonymous walk-in"
              sx={{ mb: 2 }}
            />
          )}

          {!anonymous && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <InvTextField
                  label="Patient name *"
                  value={patient.name}
                  onChange={(e) => setPatient((p) => ({ ...p, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InvTextField
                  label="Mobile"
                  value={patient.phone}
                  onChange={(e) => setPatient((p) => ({ ...p, phone: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InvTextField
                  label="Age"
                  type="number"
                  value={patient.age}
                  onChange={(e) => setPatient((p) => ({ ...p, age: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InvSelect
                  label="Gender"
                  value={patient.gender}
                  options={GENDER_OPTIONS}
                  onChange={(e) => setPatient((p) => ({ ...p, gender: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InvTextField
                  label="Doctor's name"
                  value={patient.doctorName}
                  onChange={(e) => setPatient((p) => ({ ...p, doctorName: e.target.value }))}
                  helperText="Optional"
                />
              </Grid>
            </Grid>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <InvButton disabled={!patientValid} onClick={() => setStep(1)}>
              Continue
            </InvButton>
          </Box>
        </SectionCard>
      )}

      {step === 1 && (
        <DirectDispenseMedicineStep
          lines={lines}
          setLines={setLines}
          storeId={storeId}
          store={store}
          isSupervisor={can(INV_ACTIONS.CONTROLLED_OVERRIDE)}
          serverTotals={null}
          pipeline={pipeline}
          patientLabel={patientLabel}
          doctorName={doctorName}
          patientDisplay={patientDisplay}
          saleFinalized={pipeline.state.saleFinalized}
          onPrepareBill={prepareBillOnly}
          onPrepareAndPay={prepareAndPay}
          onNewDispense={hardReset}
          saleRemarks={buildRemarks()}
          requireStoreForSearch={false}
        />
      )}
    </Box>
  );
}
